import { useEffect, useState } from "react";
import ApiService from "../../data/ApiService";
import MapPolygonUtils from "../../utils/MapPolygonUtils";
import { getCachedTerminalGeoJSON, setCachedTerminalGeoJSON } from "../../utils/TerminalPolygonCache";

const FETCH_BATCH_SIZE = 5;

function getMarkerId(marker) {
  return marker?.terminalID ?? marker?.erezSKU;
}

function chunkArray(items, size) {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function mergePolygonIntoMarker(marker, terminalGeoJSON) {
  if (!terminalGeoJSON) return marker;
  return { ...marker, terminalGeoJSON };
}

function applyPolygonsToMarkers(markers, polygonByMarkerId) {
  return markers.map((marker) => {
    const terminalGeoJSON = polygonByMarkerId[getMarkerId(marker)];
    return terminalGeoJSON ? mergePolygonIntoMarker(marker, terminalGeoJSON) : marker;
  });
}

function splitMarkersByCache(markersToFetch, modelVersionInt) {
  const polygonByMarkerId = {};
  const markersNeedingFetch = [];

  markersToFetch.forEach((marker) => {
    const cachedTerminalGeoJSON = getCachedTerminalGeoJSON(marker.terminalID, modelVersionInt);
    if (cachedTerminalGeoJSON !== undefined) {
      polygonByMarkerId[getMarkerId(marker)] = cachedTerminalGeoJSON;
      return;
    }
    markersNeedingFetch.push(marker);
  });

  return { polygonByMarkerId, markersNeedingFetch };
}

async function fetchTerminalGeoJSON(marker, modelVersionInt) {
  const cachedTerminalGeoJSON = getCachedTerminalGeoJSON(marker.terminalID, modelVersionInt);
  if (cachedTerminalGeoJSON !== undefined) {
    return cachedTerminalGeoJSON;
  }

  const fetchedData = await ApiService.getTerminalData(marker.terminalID, modelVersionInt);
  const terminalGeoJSON = fetchedData?.nationalModelForTerminal_data?.terminalGeoJSON ?? null;
  setCachedTerminalGeoJSON(marker.terminalID, modelVersionInt, terminalGeoJSON);
  return terminalGeoJSON;
}

async function fetchMarkerPolygon(marker, modelVersionInt) {
  try {
    const terminalGeoJSON = await fetchTerminalGeoJSON(marker, modelVersionInt);
    return { markerId: getMarkerId(marker), terminalGeoJSON };
  } catch (error) {
    console.error(`Error fetching terminal polygon for ${marker.terminalID}:`, error);
    return { markerId: getMarkerId(marker), terminalGeoJSON: null };
  }
}

export function useMapMarkersPolygons(
  markers = [],
  { modelVersionInt = 0, focusTerminalID = null, fetchAllMissingPolygons = false } = {}
) {
  const [enrichedMarkers, setEnrichedMarkers] = useState(markers);

  useEffect(() => {
    if (!markers?.length) {
      setEnrichedMarkers([]);
      return;
    }

    const markersMissingPolygon = markers.filter((marker) => !MapPolygonUtils.parseTerminalGeoJSON(marker));
    if (markersMissingPolygon.length === 0) {
      setEnrichedMarkers(markers);
      return;
    }

    let markersToFetch = [];

    if (fetchAllMissingPolygons) {
      markersToFetch = markersMissingPolygon;
    } else {
      const focusMarker = focusTerminalID ? markers.find((marker) => getMarkerId(marker) === focusTerminalID) : null;
      const singleMarker = markers.length === 1 ? markers[0] : null;
      const markerToFetch = focusMarker ?? singleMarker;

      if (!markerToFetch || MapPolygonUtils.parseTerminalGeoJSON(markerToFetch)) {
        setEnrichedMarkers(markers);
        return;
      }

      markersToFetch = [markerToFetch];
    }

    const { polygonByMarkerId, markersNeedingFetch } = splitMarkersByCache(markersToFetch, modelVersionInt);

    if (Object.keys(polygonByMarkerId).length > 0) {
      setEnrichedMarkers(applyPolygonsToMarkers(markers, polygonByMarkerId));
    }

    if (markersNeedingFetch.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const mergedPolygonByMarkerId = { ...polygonByMarkerId };
        const batches = chunkArray(markersNeedingFetch, FETCH_BATCH_SIZE);

        for (const batch of batches) {
          if (cancelled) return;

          const batchResults = await Promise.all(batch.map((marker) => fetchMarkerPolygon(marker, modelVersionInt)));

          if (cancelled) return;

          batchResults.forEach(({ markerId, terminalGeoJSON }) => {
            if (terminalGeoJSON) {
              mergedPolygonByMarkerId[markerId] = terminalGeoJSON;
            }
          });

          setEnrichedMarkers(applyPolygonsToMarkers(markers, mergedPolygonByMarkerId));
        }
      } catch (error) {
        console.error("Error fetching terminal polygons:", error);
        if (!cancelled) {
          setEnrichedMarkers(applyPolygonsToMarkers(markers, polygonByMarkerId));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [markers, modelVersionInt, focusTerminalID, fetchAllMissingPolygons]);

  return enrichedMarkers;
}
